// API service layer for handling API requests
import axios from 'axios';

// API base URL
const API_BASE_URL = 'https://api.ezgostore.com/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config: any) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  }
);

// Types
export interface User {
  id: string;
  fullname: string;
  email: string;
  balance: string;
  vipLevel: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  subscriptionExpiryDate?: string | null;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  fullname: string;
  email: string;
  password: string;
  verificationCode: string;
}

export interface SubscriptionResult {
  success: boolean;
  message: string;
  user: User;
}

export interface TransactionData {
  id?: string;
  type: 'add' | 'subtract';
  amount: string;
  description: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface WalletData {
  address: string;
  walletIndex?: number;
}

// API functions
export const sendVerificationCode = async (email: string): Promise<boolean> => {
  try {
    await api.post('/auth/verification', { email });
    return true;
  } catch (error) {
    throw new Error('Failed to send verification code. Please try again.');
  }
};

export const loginUser = async (credentials: LoginCredentials): Promise<string> => {
  try {
    const response = await api.post('/auth/login', credentials);
    return response.data.token;
  } catch (error) {
    throw new Error('Login failed. Please check your credentials.');
  }
};

export const registerUser = async (data: RegisterData): Promise<boolean> => {
  try {
    await api.post('/auth/register', data);
    return true;
  } catch (error) {
    throw new Error('Registration failed. Please try again.');
  }
};

export const getUserData = async (): Promise<User> => {
  try {
    const response = await api.get('/user/me');
    console.log('API response for /user/me:', response.data);
    
    // Check if the response data is nested inside a 'user' property
    if (response.data && response.data.user) {
      return response.data.user;
    }
    
    // If the response data has the expected structure but with different property names
    // map them to our expected property names
    if (response.data) {
      const userData = { ...response.data };
      
      // Map common alternative property names
      if (userData.name && !userData.fullname) userData.fullname = userData.name;
      if (userData.username && !userData.fullname) userData.fullname = userData.username;
      if (userData.full_name && !userData.fullname) userData.fullname = userData.full_name;
      
      // Ensure all required properties exist
      if (!userData.id) userData.id = 'unknown';
      if (!userData.fullname) userData.fullname = 'Unknown User';
      if (!userData.email) userData.email = 'No Email';
      if (!userData.balance) userData.balance = '0';
      if (userData.vipLevel === undefined) userData.vipLevel = 0;
      if (userData.active === undefined) userData.active = true;
      if (!userData.createdAt) userData.createdAt = new Date().toISOString();
      if (!userData.updatedAt) userData.updatedAt = new Date().toISOString();
      
      return userData as User;
    }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching user data:', error);
    throw new Error('Failed to fetch user data.');
  }
};

export const purchaseSubscription = async (): Promise<SubscriptionResult> => {
  try {
    const response = await api.post('/subscription');
    return response.data;
  } catch (error) {
    throw new Error('Failed to purchase subscription.');
  }
};

export const getSubscriptionInfo = async (): Promise<any> => {
  try {
    const response = await api.get('/subscription');
    return response.data;
  } catch (error) {
    throw new Error('Failed to get subscription information.');
  }
};

export const cancelSubscription = async (): Promise<any> => {
  try {
    const response = await api.delete('/subscription');
    return response.data;
  } catch (error) {
    throw new Error('Failed to cancel subscription.');
  }
};

export const updateVipLevel = async (vipLevel: number): Promise<any> => {
  try {
    const response = await api.put('/subscription/vip-level', { vipLevel });
    return response.data;
  } catch (error) {
    throw new Error('Failed to update VIP level.');
  }
};

export const createTransaction = async (data: TransactionData): Promise<any> => {
  try {
    const response = await api.post('/transactions', data);
    return response.data;
  } catch (error) {
    throw new Error('Failed to create transaction.');
  }
};

export const getTransactions = async (params?: {
  page?: number;
  pageSize?: number;
  type?: 'add' | 'subtract';
  startDate?: string;
  endDate?: string;
}): Promise<any> => {
  try {
    const response = await api.get('/transactions', { params });
    return response.data;
  } catch (error) {
    throw new Error('Failed to get transactions.');
  }
};

export const getTransaction = async (id: string): Promise<any> => {
  try {
    const response = await api.get(`/transactions/${id}`);
    return response.data;
  } catch (error) {
    throw new Error('Failed to get transaction.');
  }
};

export const updateTransaction = async (id: string, data: Partial<TransactionData>): Promise<any> => {
  try {
    const response = await api.put(`/transactions/${id}`, data);
    return response.data;
  } catch (error) {
    throw new Error('Failed to update transaction.');
  }
};

export const deleteTransaction = async (id: string): Promise<any> => {
  try {
    const response = await api.delete(`/transactions/${id}`);
    return response.data;
  } catch (error) {
    throw new Error('Failed to delete transaction.');
  }
};

export const getWalletData = async (): Promise<WalletData> => {
  try {
    const response = await api.get('/wallet/me');
    console.log('API response for /wallet/me:', response.data);
    
    if (response.data) {
      // Map the API response to our WalletData interface
      const walletData: WalletData = {
        address: response.data.tronUsdtAddress || '',
        walletIndex: response.data.walletIndex
      };
      
      return walletData;
    }
    
    return { address: '' };
  } catch (error) {
    console.error('Error fetching wallet data:', error);
    throw new Error('Failed to fetch wallet data.');
  }
};
